import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Square, Send, Clock, CheckCircle2, Loader2, AlertTriangle, ChevronRight, RefreshCw } from "lucide-react";
import api, { createAbortSignal } from "../../api";
import SalesCertProctorHud from "../../components/SalesCertProctorHud";
import MicCheckGate from "../../components/MicCheckGate";
import SecurityHardStop from "../../components/SecurityHardStop";
import { IntegrityBanner, IntegrityWatermark } from "../../components/IntegrityOverlay";
import { nextSalescertSection, isAttemptExhaustedError } from "../../utils/salescertNav";
import { trackClick } from "../../hooks/useActivityLogger";

// Parse a security-hold id out of a 423 detail string. Backend uses the
// convention "SECURITY_HOLD:<uuid> · <human message>" so we can inline the
// hard-stop screen without a separate polling contract.
const extractHoldId = (detail) => {
  const m = /SECURITY_HOLD:([0-9a-fA-F-]{36})/.exec(String(detail || ""));
  return m ? m[1] : null;
};

/** Sales Pitch attempt page — mic-check → 1-min prep → 4-min recorded audio → Claude scoring. */
export default function SalesPitchAttempt() {
  const nav = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading|mic_check|prep|record|uploading|scored|error|hard_stop
  const [remaining, setRemaining] = useState(0);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [holdId, setHoldId] = useState(null);
  // Safeguard: even though /participant/miccheck/transcribe now enrols
  // synchronously, we double-check with /participant/salescert/status before
  // moving to the prep phase. If the status endpoint doesn't confirm the
  // enrolment, we tell the candidate to re-do the mic-check rather than
  // letting them record a pitch that would fail voice verification at submit.
  const [enrolCheck, setEnrolCheck] = useState({ inFlight: false, error: "" });
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  useEffect(() => {
    // Cohort is derived from the logged-in user's role_level on the server
    // (L1 → IC, L2+ → Managerial). No cohort override from the client.
    api.get("/participant/salespitch/next")
      .then((r) => {
        setPrompt(r.data);
        setPhase("mic_check");
        trackClick("salescert_pitch_start", { prompt_id: r.data.id, cohort: r.data.cohort });
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || String(e);
        // 423 Locked = security hold pending. Show hard-stop screen with
        // polling instead of the generic error page so the candidate has a
        // clear next step (wait for admin approval).
        if (e?.response?.status === 423) {
          const hid = extractHoldId(detail);
          if (hid) {
            setHoldId(hid);
            setPhase("hard_stop");
            return;
          }
        }
        // Exhausted / forfeited attempts → silently bounce to the hub;
        // the section card there already communicates the state.
        if (isAttemptExhaustedError(e)) {
          nav("/participant/salescert", { replace: true });
          return;
        }
        setErr(detail);
        setPhase("error");
      });
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (phase !== "prep" && phase !== "record") return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          if (phase === "prep") startRecording();
          else if (phase === "record") stopRecording();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // Single-attempt integrity guard — warn on close / refresh / nav while the
  // candidate is in mic-check, prep, or recording. The server-side stub
  // created by /salespitch/next will be auto-forfeited on the next GET.
  useEffect(() => {
    if (!["mic_check", "prep", "record"].includes(phase)) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // MediaRecorder mimeType — the browser will pick the best supported codec.
      // Chrome prefers audio/webm;codecs=opus which Whisper handles cleanly.
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];
      const mime = preferred.find((t) => !t || (window.MediaRecorder && MediaRecorder.isTypeSupported(t))) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => stream.getTracks().forEach((t) => t.stop());
      // Emit a chunk every second — without a timeslice some browsers only
      // fire ondataavailable ONCE at stop, and if the browser/tab is throttled
      // the recording buffer can be truncated. Per-second chunks are safe.
      // iter43 — wrap rec.start() so NotSupportedError / InvalidStateError
      // (e.g. a stale MediaRecorder from a previous section still holding
      // the mic) can never surface as a raw React dev-overlay. On failure
      // we release this stream and prompt the candidate to retry.
      try {
        rec.start(1000);
      } catch (e) {
        stream.getTracks().forEach((t) => t.stop());
        setErr("Microphone is temporarily busy — please tap Start recording again in a moment.");
        setPhase("prep");
        return;
      }
      mediaRef.current = rec;
      startRef.current = Date.now();
      setPhase("record");
      setRemaining(prompt.record_seconds);
    } catch (e) {
      setErr("Microphone access denied. Please allow mic access and reload.");
      setPhase("error");
    }
  };

  const proceedToPrep = async () => {
    // Double-check enrolment via /salescert/status before advancing to prep.
    // The mic-check endpoint already awaits the embedding + Mongo upsert, so
    // this is a belt-and-braces guard: if the status endpoint doesn't confirm
    // the enrolment (network glitch, DB write failure), we make the candidate
    // redo the mic-check instead of letting them record a pitch that would
    // then be rejected by _verify_voice_or_reject at submit time.
    setEnrolCheck({ inFlight: true, error: "" });
    try {
      const status = await api.get("/participant/salescert/status").then((r) => r.data);
      if (!status?.voice_enrolled) {
        setEnrolCheck({
          inFlight: false,
          error: "We couldn't confirm your voice enrolment. Please try the mic-check again.",
        });
        return;
      }
    } catch (e) {
      setEnrolCheck({
        inFlight: false,
        error: e?.response?.data?.detail || "Enrolment check failed. Please try the mic-check again.",
      });
      return;
    }
    setEnrolCheck({ inFlight: false, error: "" });
    setPhase("prep");
    setRemaining(prompt.prep_seconds);
  };

  const stopRecording = async () => {
    if (!mediaRef.current || mediaRef.current.state !== "recording") return;
    setPhase("uploading");
    trackClick("salescert_pitch_submit", { prompt_id: prompt?.id, duration_sec: Math.round((Date.now() - startRef.current) / 1000) });
    mediaRef.current.onstop = async () => {
      mediaRef.current.stream?.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const fd = new FormData();
      fd.append("prompt_id", prompt.id);
      fd.append("audio", blob, "pitch.webm");
      // 90 s abort budget for /salespitch/submit. Whisper STT on a 2-minute
      // WebM upload over a slow mobile link occasionally pushes past the 30 s
      // default axios timeout, at which point the browser drops the response
      // even though the backend has already persisted the transcript. If we
      // time out, fall back to GET /salespitch/latest-result to recover the
      // saved submission (mirrors the /roleplay/latest-turn recovery pattern).
      const { signal, cancel } = createAbortSignal(90_000);
      const recoverFromLatest = async () => {
        try {
          const r2 = await api.get("/participant/salespitch/latest-result");
          const status2 = await api.get("/participant/salescert/status").then((x) => x.data).catch(() => null);
          const nxt2 = nextSalescertSection(status2, "sales_pitch");
          setResult({ ...r2.data, next: nxt2 });
          setPhase("scored");
          return true;
        } catch {
          return false;
        }
      };
      try {
        const r = await api.post("/participant/salespitch/submit", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0, // rely on AbortController budget above
          signal,
        });
        // Compute next section for the scored screen CTA
        const status = await api.get("/participant/salescert/status").then((x) => x.data).catch(() => null);
        const nxt = nextSalescertSection(status, "sales_pitch");
        setResult({ ...r.data, next: nxt });
        setPhase("scored");
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 423) {
          const hid = extractHoldId(detail);
          if (hid) { setHoldId(hid); setPhase("hard_stop"); return; }
        }
        const isTimeoutOrAbort = e?.code === "ECONNABORTED"
          || e?.name === "CanceledError"
          || e?.name === "AbortError"
          || String(e?.message || "").toLowerCase().includes("timeout");
        if (isTimeoutOrAbort) {
          const recovered = await recoverFromLatest();
          if (recovered) return;
        }
        setErr(detail || String(e));
        setPhase("error");
      } finally {
        cancel();
      }
    };
    mediaRef.current.stop();
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (phase === "loading") return <div className="p-10 text-sm text-[#878787]">Loading your prompt…</div>;
  if (phase === "hard_stop") return (
    <SecurityHardStop
      holdId={holdId}
      onResolvedResume={() => window.location.reload()}
      onResolvedReset={() => window.location.reload()}
    />
  );
  if (phase === "error") return (
    <div className="max-w-2xl mx-auto p-8 fade-in" data-testid="pitch-error">
      <AlertTriangle className="text-[#EE2737] mb-3" size={28} />
      <h2 className="font-heading text-2xl font-bold mb-2">Something went wrong</h2>
      <p className="text-sm text-[#525252] mb-6">{err}</p>
      <button onClick={() => nav("/participant")} className="btn btn-secondary">Back to dashboard</button>
    </div>
  );

  // ---- Mic check — before the timed pitch, prove the mic is actually capturing ----
  if (phase === "mic_check") return (
    <div className="max-w-3xl mx-auto p-6" data-testid="pitch-mic-check-wrapper">
      <MicCheckGate
        title="Let's check your mic before the pitch"
        subtitle="Before the timed pitch, we'll record 5 seconds of you speaking a test phrase and read it back so you know your mic is working. This is not scored."
        testPhrase="Testing one two three — this is my voice check for the pitch."
        proceedLabel={enrolCheck.inFlight ? "Confirming enrolment…" : "Perfect — start my pitch"}
        proceedDisabled={enrolCheck.inFlight}
        onPass={proceedToPrep}
        onSecurityHold={({ holdId: hid }) => { setHoldId(hid); setPhase("hard_stop"); }}
      />
      {enrolCheck.error && (
        <div
          className="mt-4 px-4 py-3 bg-[#FEE2E2] border-l-4 border-[#EE2737] text-[13px] text-[#991B1B] flex items-start gap-2"
          data-testid="pitch-enrol-error"
        >
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">{enrolCheck.error}</div>
            <div className="text-xs mt-1 text-[#7F1D1D]">Retry the 5-second mic-check above to re-enrol your voice.</div>
          </div>
        </div>
      )}
    </div>
  );

  if (phase === "scored") {
    const released = result.results_released !== false;
    if (!released) return (
      <div className="max-w-3xl mx-auto p-8 space-y-6 fade-in" data-testid="pitch-scored">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="text-[#059669]" size={28} />
          <h2 className="font-heading text-3xl font-bold">Pitch recorded</h2>
        </div>
        <div className="card p-6" data-testid="pitch-recorded-embargo">
          <div className="overline mb-2">RESPONSE RECORDED</div>
          <p className="text-sm text-[#0A0A0A] leading-relaxed">
            {result.message || "Your Sales Pitch has been recorded. VI L&D will share your consolidated report and certificate once the assessment window closes."}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {result.can_retake && (
            <button
              onClick={() => window.location.reload()}
              className="btn btn-secondary text-sm justify-center"
              data-testid="pitch-retake"
            >
              <RefreshCw size={14} /> Take Second Attempt
            </button>
          )}
          {result.next ? (
            <button
              onClick={() => nav(result.next.href)}
              className="btn btn-primary text-sm justify-center"
              data-testid="pitch-next-section"
            >
              Proceed to {result.next.label} <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={() => nav("/participant/salescert")}
              className="btn btn-primary text-sm justify-center"
              data-testid="pitch-back"
            >
              Back to certification hub <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    );
    return (
    <div className="max-w-3xl mx-auto p-8 space-y-6 fade-in" data-testid="pitch-scored">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="text-[#059669]" size={28} />
        <h2 className="font-heading text-3xl font-bold">Pitch submitted</h2>
      </div>
      <div className="card p-6">
        <div className="overline mb-2">SCORE</div>
        <div className="flex items-baseline gap-3">
          <div className="text-5xl font-black text-[#EE2737]" data-testid="pitch-marks">{result.marks_awarded}</div>
          <div className="text-sm text-[#525252]">/ {result.marks_max} marks</div>
        </div>
        <div className="text-xs text-[#878787] mt-1">Rubric score: {result.score_1_to_5} / 5</div>
      </div>
      <div className="card p-6">
        <div className="overline mb-2">FEEDBACK</div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed" data-testid="pitch-feedback">{result.feedback}</p>
      </div>
      <details className="card p-6">
        <summary className="cursor-pointer text-xs uppercase tracking-widest text-[#525252] font-bold">View your transcript</summary>
        <p className="text-sm text-[#525252] mt-3 leading-relaxed whitespace-pre-wrap" data-testid="pitch-transcript">{result.transcript}</p>
      </details>
      <div className="grid sm:grid-cols-2 gap-3">
        {result.can_retake && (
          <button
            onClick={() => window.location.reload()}
            className="btn btn-secondary text-sm justify-center"
            data-testid="pitch-retake"
          >
            <RefreshCw size={14} /> Take Second Attempt
          </button>
        )}
        {result.next ? (
          <button
            onClick={() => nav(result.next.href)}
            className="btn btn-primary text-sm justify-center"
            data-testid="pitch-next-section"
          >
            Proceed to {result.next.label} <ChevronRight size={14} />
          </button>
        ) : (
          <button
            onClick={() => nav("/participant/salescert/report")}
            className="btn btn-primary text-sm justify-center"
            data-testid="pitch-view-report"
          >
            View report <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6 fade-in relative" data-testid="pitch-attempt">
      <SalesCertProctorHud />
      <IntegrityBanner />
      <IntegrityWatermark testid="pitch-watermark" />

      {/* Sticky timer bar — keeps the phase + countdown visible while the
          candidate scrolls the prompt or notes area. Phase-coloured (grey
          for prep, red for live recording). */}
      {(phase === "prep" || phase === "record") && (
        <div
          className={`sticky top-0 z-30 -mx-6 px-6 py-3 backdrop-blur-sm border-b flex items-center justify-between gap-3 ${
            phase === "record" ? "bg-[#FEE2E2]/95 border-[#EE2737]" : "bg-white/95 border-[#E5E7EB]"
          }`}
          data-testid="pitch-sticky-header"
        >
          <div className="min-w-0">
            <div className={`overline ${phase === "record" ? "!text-[#991B1B]" : ""}`}>
              {phase === "record" ? "● RECORDING" : "PREPARATION · NO RECORDING YET"}
            </div>
            <div className="text-[11px] text-[#525252] mt-0.5 truncate">
              {phase === "record" ? "Recording auto-stops at 00:00" : "Recording auto-starts when the timer hits zero"}
            </div>
          </div>
          <div className={`font-heading font-black text-2xl flex items-center gap-2 px-3 py-1.5 ${
            phase === "record" ? "bg-[#EE2737] text-white" : "bg-[#0A0A0A] text-white"
          }`}>
            {phase === "record" ? <Mic size={20} /> : <Clock size={20} />}
            <span data-testid={`pitch-sticky-timer-${phase}`}>{mmss(remaining)}</span>
          </div>
        </div>
      )}

      <div>
        <div className="overline mb-2">AI-LED SALES PITCH · {prompt.cohort} · {prompt.marks} MARKS</div>
        <h1 className="font-heading text-3xl font-bold">Prepare, then deliver your pitch</h1>
      </div>
      {/* Single-attempt integrity warning */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#FEF3C7] border-l-4 border-[#F59E0B] text-[11px] text-[#92400E]" data-testid="pitch-single-attempt-warning">
        <AlertTriangle size={14} className="flex-shrink-0" />
        <span><strong>Single attempt only.</strong> Do not close, reload, or navigate away — leaving forfeits the section (0 marks).</span>
      </div>
      <div className="card p-6" data-testid="pitch-prompt">
        <div className="overline mb-2">BUSINESS SITUATION</div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">{prompt.prompt}</p>
        {prompt.prep_instructions && (
          <p className="text-xs text-[#525252] mt-4 italic">{prompt.prep_instructions}</p>
        )}
      </div>

      {phase === "prep" && (
        <div className="card p-6 flex items-center justify-between gap-4" data-testid="pitch-prep-phase">
          <div>
            <div className="overline mb-1">PREPARATION · NO RECORDING YET</div>
            <p className="text-sm text-[#525252]">Recording auto-starts when the timer hits zero, or click Start now.</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black text-[#0A0A0A]" data-testid="pitch-prep-timer"><Clock size={22} className="inline mr-1" />{mmss(remaining)}</div>
            <button onClick={startRecording} className="btn btn-primary mt-2 text-xs" data-testid="pitch-start-record"><Mic size={12} /> Start recording now</button>
          </div>
        </div>
      )}

      {phase === "record" && (
        <div className="card p-6 flex items-center justify-between gap-4 border-2 border-[#EE2737]" data-testid="pitch-record-phase">
          <div>
            <div className="overline mb-1 text-[#EE2737]">● RECORDING</div>
            <p className="text-sm text-[#525252]">Speak clearly. Recording auto-stops at 00:00, or click Stop when done.</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black text-[#EE2737]" data-testid="pitch-record-timer"><Mic size={22} className="inline mr-1" />{mmss(remaining)}</div>
            <button onClick={stopRecording} className="btn btn-secondary mt-2 text-xs" data-testid="pitch-stop-record"><Square size={12} /> Stop & submit</button>
          </div>
        </div>
      )}

      {phase === "uploading" && (
        <div className="card p-6 flex items-center gap-3" data-testid="pitch-uploading">
          <Loader2 className="animate-spin text-[#EE2737]" size={20} />
          <span className="text-sm text-[#525252]">Uploading, transcribing, and scoring your pitch (~15–25s)…</span>
        </div>
      )}
    </div>
  );
}
